import React, { useCallback, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import {
    Button,
    Card,
    Col,
    Form,
    FormProps,
    Grid,
    Input,
    Row,
    Select,
    Space,
    Table,
    Tag,
} from 'antd';
import {
    DownloadOutlined,
    DownOutlined,
    FilterOutlined,
    ReloadOutlined,
    SearchOutlined,
    UpOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { ColumnsType } from 'antd/lib/table';
import { normalizeDoc } from '@/utils/doc';
import { PlugPag } from '@inhouse-market/sdk';
import { ExportReports } from '@/Export';
import { useAuth } from '@/contexts/Auth';
import { useExcelExport } from '@/store/salles/GetExcel/useExcelExport';
import { FilterBlock } from '@/components/FilterBlock';

import { useGetTotemReport } from '../../../store/Totem/useGetTotemReport';
import {
    EMPTY,
    PILL_BLUE,
    PILL_GREEN,
    PILL_GREY,
    PILL_RED,
    SPLIT_STATUS_OPTIONS,
    SPLIT_TYPE_OPTIONS,
} from './const';

interface FilterFormValues {
    totemId?: string;
    storeId?: string;
    shopmanagerId?: string;
    doc?: string;
    plugpagEmail?: string;
    storeName?: string;
    splitStatus?: PlugPag.EnumSplitStatus;
    splitType?: PlugPag.EnumSplitType;
}

const onlyDigits = (value?: string) => (value ?? '').replace(/\D/g, '');

const parseOptionalInt = (value?: string) => {
    if (value === undefined || value === null || String(value).trim() === '')
        return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
};

const formatTextOrEmpty = (value?: string | null) => {
    const text = (value ?? '').trim();
    return text || EMPTY;
};

const formatHardwareModel = (hardwareModel?: string | null) => {
    const model = (hardwareModel ?? '').trim();
    if (model.toLowerCase() === 'unknown') return EMPTY;
    return formatTextOrEmpty(model);
};

const formatDoc = (doc?: string | null) => {
    const clean = normalizeDoc(doc ?? '');
    if (clean.length !== 14) return formatTextOrEmpty(doc);
    return `${clean.slice(0, 2)}.${clean.slice(2, 5)}.${clean.slice(
        5,
        8,
    )}/${clean.slice(8, 12)}-${clean.slice(12)}`;
};

const formatDate = (iso?: string | null) => {
    const value = (iso ?? '').trim();
    if (!value) return EMPTY;
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : EMPTY;
};

const formatSplitPercentage = (value?: number | null) => {
    const parsed = Number(value ?? 0);
    if (Number.isNaN(parsed) || parsed === 0) return EMPTY;
    return `${parsed.toFixed(1).replace('.', ',')}%`;
};

const formatTotal = (total?: number) => (total ?? 0).toLocaleString('pt-BR');

const isSmartByAndroidUid = (androidUid?: string | null) => {
    const value = (androidUid ?? '').trim().toUpperCase();
    return (
        value.startsWith('600') ||
        value.startsWith('PBA') ||
        value.startsWith('PB3')
    );
};

const renderDevice = (record: PlugPag.TotemReportModel) => {
    if (isSmartByAndroidUid(record.android_uid)) return 'Smart';
    return record.type ?? EMPTY;
};

const isTerminalSplit = (record: PlugPag.TotemReportModel) =>
    record.shopmanager?.pagbank_split_type === PlugPag.EnumSplitType.terminal;

const renderSplitType = (record: PlugPag.TotemReportModel) => {
    if (isTerminalSplit(record)) {
        return <Tag style={PILL_BLUE}>Terminal</Tag>;
    }
    if (record.shopmanager?.pagbank_split_type === PlugPag.EnumSplitType.post) {
        return <Tag style={PILL_GREEN}>Pós</Tag>;
    }
    return <Tag style={PILL_GREY}>Não definido</Tag>;
};

const getSplitPercentage = (record: PlugPag.TotemReportModel) =>
    isTerminalSplit(record)
        ? Number(record.split_pagbank_smart ?? 0)
        : Number(record.store?.pagbankSplitPercentage ?? 0);

const renderSplitStatus = (record: PlugPag.TotemReportModel) => {
    if (getSplitPercentage(record) > 0) {
        return <Tag style={PILL_GREEN}>Ativo</Tag>;
    }
    if (record.split_removed_at) {
        return <Tag style={PILL_RED}>Retirado</Tag>;
    }
    return <Tag style={PILL_GREY}>Sem split</Tag>;
};

const renderAuthenticated = (record: PlugPag.TotemReportModel) => {
    if (record.lastLog?.authenticated) {
        return <Tag style={PILL_GREEN}>AUTENTICADO</Tag>;
    }
    return <Tag style={PILL_RED}>NÃO AUTENTICADO</Tag>;
};

const TotemsReport: React.FC = () => {
    const breakpoints = Grid.useBreakpoint();
    const isMobile = !breakpoints.md;
    const [form] = Form.useForm<FilterFormValues>();
    const [params, setParams] = useState<PlugPag.ReportRequest>({
        page: 1,
        perPage: 10,
    });
    const [openPanel, setOpenPanel] = useState<'filters' | 'export' | null>(
        null,
    );
    const showFilters = openPanel === 'filters';
    const showExport = openPanel === 'export';

    const togglePanel = (panel: 'filters' | 'export') =>
        setOpenPanel((prev) => (prev === panel ? null : panel));

    const { data, isFetching, refetch } = useGetTotemReport(params);

    const { data: authData } = useAuth();
    const { data: exportsData } = useExcelExport({
        userId: authData?.user?.id,
        type: 'totem',
    });
    const exportsCount = exportsData?.data.length ?? 0;

    const handlePaginationChange = useCallback(
        (page: number, pageSize?: number) => {
            flushSync(() => {
                setParams((prev) => ({
                    ...prev,
                    page,
                    perPage: pageSize !== undefined ? pageSize : prev.perPage,
                }));
            });
        },
        [],
    );

    const columns: ColumnsType<PlugPag.TotemReportModel> = useMemo(
        () => [
            {
                title: 'ID lojista',
                dataIndex: 'shopmanager_id',
                width: 110,
                fixed: isMobile ? undefined : ('left' as const),
                render: (_: unknown, record) => record.shopmanager_id ?? EMPTY,
            },
            {
                title: 'CNPJ',
                width: 180,
                render: (_: unknown, record) =>
                    formatDoc(record.shopmanager?.doc),
            },
            {
                title: 'E-mail PagBank',
                width: 230,
                ellipsis: true,
                render: (_: unknown, record) =>
                    formatTextOrEmpty(record.shopmanager?.plugpag_email),
            },
            {
                title: 'ID loja',
                dataIndex: 'store_id',
                width: 100,
            },
            {
                title: 'Loja',
                width: 220,
                ellipsis: true,
                render: (_: unknown, record) =>
                    formatTextOrEmpty(record.store?.name),
            },
            {
                title: 'ID Totem',
                dataIndex: 'id',
                width: 100,
            },
            {
                title: 'Totem',
                width: 190,
                ellipsis: true,
                render: (_: unknown, record) => formatTextOrEmpty(record.nome),
            },
            {
                title: 'Modelo',
                dataIndex: 'hardware_model',
                width: 130,
                ellipsis: true,
                render: (_: unknown, record) =>
                    formatHardwareModel(record.hardware_model),
            },
            {
                title: 'Dispositivo',
                width: 120,
                render: (_: unknown, record) => renderDevice(record),
            },
            {
                title: 'Status',
                width: 170,
                render: (_: unknown, record) => renderAuthenticated(record),
            },
            {
                title: 'Tipo de split',
                width: 130,
                render: (_: unknown, record) => renderSplitType(record),
            },
            {
                title: 'Situação do split',
                width: 150,
                render: (_: unknown, record) => renderSplitStatus(record),
            },
            {
                title: 'Taxa de split',
                width: 120,
                render: (_: unknown, record) =>
                    formatSplitPercentage(getSplitPercentage(record)),
            },
            {
                title: 'Início do split',
                width: 140,
                render: (_: unknown, record) =>
                    formatDate(record.split_started_at),
            },
            {
                title: 'Retirada do split',
                width: 150,
                render: (_: unknown, record) =>
                    formatDate(record.split_removed_at),
            },
        ],
        [isMobile],
    );

    const onSearch: FormProps<FilterFormValues>['onFinish'] = (values) => {
        setParams({
            page: 1,
            perPage: params.perPage,
            totemId: parseOptionalInt(values.totemId),
            storeId: parseOptionalInt(values.storeId),
            shopmanagerId: parseOptionalInt(values.shopmanagerId),
            doc: values.doc?.trim() || undefined,
            plugpagEmail: values.plugpagEmail?.trim() || undefined,
            storeName: values.storeName?.trim() || undefined,
            splitStatus: values.splitStatus || undefined,
            splitType: values.splitType || undefined,
        });
    };

    const onReset = () => {
        form.resetFields();
        setParams({ page: 1, perPage: 10 });
    };

    const total = data?.meta.total;
    const showing = data?.data.length ?? 0;

    return (
        <Row style={{ padding: '0px 20px' }}>
            <Col span={24}>
                <Card size="small">
                    <Row justify="space-between" align="middle" gutter={[8, 8]}>
                        <Col>
                            <Space wrap>
                                <Button
                                    icon={<FilterOutlined />}
                                    type={showFilters ? 'primary' : 'default'}
                                    ghost={showFilters}
                                    onClick={() => togglePanel('filters')}
                                >
                                    Filtros{' '}
                                    {showFilters ? (
                                        <UpOutlined />
                                    ) : (
                                        <DownOutlined />
                                    )}
                                </Button>
                                <Button
                                    icon={<DownloadOutlined />}
                                    type={showExport ? 'primary' : 'default'}
                                    ghost={showExport}
                                    onClick={() => togglePanel('export')}
                                >
                                    Exportar{' '}
                                    {exportsCount > 0 && (
                                        <Tag
                                            style={{
                                                borderRadius: 10,
                                                marginInlineEnd: 4,
                                            }}
                                        >
                                            {exportsCount}
                                        </Tag>
                                    )}
                                    {showExport ? (
                                        <UpOutlined />
                                    ) : (
                                        <DownOutlined />
                                    )}
                                </Button>
                            </Space>
                        </Col>
                        <Col>
                            <Button
                                icon={<ReloadOutlined />}
                                loading={isFetching}
                                onClick={() => refetch()}
                            >
                                Atualizar
                            </Button>
                        </Col>
                    </Row>
                </Card>

                {showFilters && (
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onSearch}
                        style={{ marginTop: 16, width: '100%' }}
                    >
                        <FilterBlock onReset={onReset} loading={isFetching}>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item<FilterFormValues>
                                    label="CNPJ"
                                    name="doc"
                                >
                                    <Input
                                        allowClear
                                        maxLength={18}
                                        prefix={<SearchOutlined />}
                                        placeholder="CNPJ completo"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item<FilterFormValues>
                                    label="E-mail PagBank"
                                    name="plugpagEmail"
                                >
                                    <Input
                                        allowClear
                                        maxLength={255}
                                        prefix={<SearchOutlined />}
                                        placeholder="E-mail PagBank completo"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item<FilterFormValues>
                                    label="ID do lojista"
                                    name="shopmanagerId"
                                    normalize={onlyDigits}
                                >
                                    <Input
                                        allowClear
                                        inputMode="numeric"
                                        maxLength={10}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item<FilterFormValues>
                                    label="ID da loja"
                                    name="storeId"
                                    normalize={onlyDigits}
                                >
                                    <Input
                                        allowClear
                                        inputMode="numeric"
                                        maxLength={10}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item<FilterFormValues>
                                    label="Loja"
                                    name="storeName"
                                    rules={[
                                        {
                                            min: 3,
                                            message: 'Mínimo 3 caracteres',
                                        },
                                    ]}
                                >
                                    <Input
                                        allowClear
                                        maxLength={255}
                                        prefix={<SearchOutlined />}
                                        placeholder="Buscar por loja"
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item<FilterFormValues>
                                    label="ID do totem"
                                    name="totemId"
                                    normalize={onlyDigits}
                                >
                                    <Input
                                        allowClear
                                        inputMode="numeric"
                                        maxLength={10}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item<FilterFormValues>
                                    label="Situação do split"
                                    name="splitStatus"
                                >
                                    <Select
                                        allowClear
                                        placeholder="Todos"
                                        options={SPLIT_STATUS_OPTIONS}
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Form.Item<FilterFormValues>
                                    label="Tipo de split"
                                    name="splitType"
                                >
                                    <Select
                                        allowClear
                                        placeholder="Todos"
                                        options={SPLIT_TYPE_OPTIONS}
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                        </FilterBlock>
                    </Form>
                )}

                {showExport && (
                    <Row style={{ marginTop: 24 }}>
                        <Col span={24}>
                            <ExportReports
                                params={{
                                    reportType: 'totem',
                                    totemId: params.totemId,
                                    shopmanagerId: params.shopmanagerId,
                                    storeId: params.storeId
                                        ? String(params.storeId)
                                        : undefined,
                                    doc: params.doc,
                                    plugpagEmail: params.plugpagEmail,
                                    storeName: params.storeName,
                                    splitStatus: params.splitStatus,
                                    splitType: params.splitType,
                                }}
                                labelItems="Totens"
                            />
                        </Col>
                    </Row>
                )}

                <Row style={{ marginTop: 24 }}>
                    <Col>
                        <span style={{ fontSize: 14 }}>
                            Mostrando {showing} de {formatTotal(total)} totens
                        </span>
                    </Col>
                </Row>
                <Row style={{ marginTop: 12 }}>
                    <Col span={24}>
                        <Table<PlugPag.TotemReportModel>
                            key={`totems-report-${params.page}-${params.perPage}`}
                            rowKey="id"
                            style={{ width: '100%' }}
                            dataSource={data?.data ?? []}
                            columns={columns}
                            loading={isFetching}
                            scroll={{ x: 'max-content' }}
                            pagination={{
                                total: total ?? 0,
                                current: params.page,
                                pageSize: params.perPage,
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '50', '100'],
                                onChange: handlePaginationChange,
                            }}
                        />
                    </Col>
                </Row>
            </Col>
        </Row>
    );
};

export default TotemsReport;
